import React, { useMemo, useState } from "react";
import {
    Users,
    Clock,
    Plane,
    Receipt,
    Bell,
    CheckCircle,
    CalendarRange,
    Download,
    FileSpreadsheet,
    LayoutDashboard,
    X,
} from "lucide-react";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";
import * as XLSX from "xlsx";
import "../DashboardStyles/DashboardManager.css";

// ── Static mock data ──────────────────────────────────────────────────────────
const teamData = {
    teamName: "Équipe Développement Frontend",
    manager: "Sophie Martin",
    totalMembers: 12,
    pendingLeaves: 4,
    ongoingMissions: 2,
    pendingExpenses: 6,
    notifications: [
        { id: 1, text: "Demande de congé - Jean Dupont (3 jours)", type: "leave" },
        { id: 2, text: "Note de frais en attente - Marie Lambert (850 MAD)", type: "expense" },
        { id: 3, text: "Mission terminée - Paul Durand", type: "mission" },
    ],
};

const PRIMARY = "#2f4595";
const PIE_COLORS = ["#2f4595", "#2f4595", "#10b981", "#2f4595"];

const TABS = [
    { id: "dashboard", label: "Tableau de bord", icon: <LayoutDashboard size={16} /> },
    { id: "etats", label: "États", icon: <FileSpreadsheet size={16} /> },
];

const STATIC_EXCEL_STATES = [
    {
        id: "etat-equipe",
        title: "État des membres de l'équipe",
        description: "Liste complète des membres de l'équipe avec leurs rôles, contrats et disponibilités.",
        color: "#2f4595",
    },
    {
        id: "etat-conges",
        title: "État des demandes de congés",
        description: "Suivi des demandes de congés de l'équipe : types, durées et statuts de validation.",
        color: "#10b981",
    },
    {
        id: "etat-depenses",
        title: "État des notes de frais",
        description: "Récapitulatif des notes de frais soumises par les membres de l'équipe.",
        color: "#2f4595",
    },
    {
        id: "etat-missions",
        title: "État des ordres de mission",
        description: "Suivi des déplacements professionnels de l'équipe et leurs statuts.",
        color: "#2f4595",
    },
];

// ── Sub-components ────────────────────────────────────────────────────────────
const StatCard = ({ label, value, icon, color, note }) => (
    <div className="dmStatCard">
        <div className="dmStatLeft">
            <div className="dmStatLabel">{label}</div>
            <div className="dmStatValue" style={{ color }}>{value}</div>
            {note && <div className="dmStatNote">{note}</div>}
        </div>
        <div className="dmStatIcon" style={{ color }}>{icon}</div>
    </div>
);

function DateFilterFields({ dateDebut, dateFin, onChangeDebut, onChangeFin }) {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: "6px 12px",
            }}
        >
            <CalendarRange size={15} color="#9ca3af" />
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label htmlFor="dm-date-debut" style={{ fontSize: 12, color: "#9ca3af" }}>Du</label>
                <input
                    id="dm-date-debut"
                    type="date"
                    value={dateDebut}
                    onChange={(e) => onChangeDebut(e.target.value)}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "4px 8px", fontSize: 13, color: "#333" }}
                />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <label htmlFor="dm-date-fin" style={{ fontSize: 12, color: "#9ca3af" }}>Au</label>
                <input
                    id="dm-date-fin"
                    type="date"
                    value={dateFin}
                    onChange={(e) => onChangeFin(e.target.value)}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: "4px 8px", fontSize: 13, color: "#333" }}
                />
            </div>
            {(dateDebut || dateFin) && (
                <button
                    type="button"
                    onClick={() => { onChangeDebut(""); onChangeFin(""); }}
                    style={{
                        background: "#f3f4f6",
                        border: "1px solid #e5e7eb",
                        borderRadius: 8,
                        padding: "4px 8px",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#2f4595",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                    }}
                    title="Réinitialiser les filtres"
                >
                    <X size={12} />
                    Effacer
                </button>
            )}
        </div>
    );
}

function ExcelStateCard({ title, description, color, onDownload }) {
    return (
        <div
            style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 14,
                padding: 18,
                display: "flex",
                flexDirection: "column",
                gap: 12,
                boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
            }}
        >
            <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div
                    style={{
                        width: 40, height: 40, minWidth: 40,
                        borderRadius: 10,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        color,
                        background: `${color}18`,
                        border: `1px solid ${color}30`,
                    }}
                >
                    <FileSpreadsheet size={20} />
                </div>
                <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "#1f2937" }}>{title}</div>
                    <div style={{ fontSize: 12.5, color: "#6b7280", marginTop: 4, lineHeight: 1.4 }}>{description}</div>
                </div>
            </div>
            <div
                style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    paddingTop: 8, borderTop: "1px dashed #eef0f2",
                }}
            >
                <span style={{ fontSize: 11.5, color: "#9ca3af", display: "flex", alignItems: "center", gap: 5 }}>
                    <Clock size={12} /> Mis à jour à la demande
                </span>
                <button
                    type="button"
                    onClick={onDownload}
                    style={{
                        display: "flex", alignItems: "center", gap: 6,
                        border: "none", borderRadius: 8, padding: "7px 12px",
                        fontSize: 12.5, fontWeight: 600, color: "#fff",
                        background: color, cursor: "pointer",
                    }}
                >
                    <Download size={14} />
                    Télécharger
                </button>
            </div>
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────
const DashboardManager = () => {
    const [activeTab, setActiveTab] = useState("dashboard");
    const [dateDebut, setDateDebut] = useState("");
    const [dateFin, setDateFin] = useState("");

    const pieData = useMemo(
        () => [
            { name: "Congés", value: 6 },
            { name: "Absences", value: 3 },
            { name: "RTT", value: 2 },
            { name: "Autres", value: 1 },
        ],
        []
    );

    const barData = useMemo(
        () => [
            { name: "Transport", value: 4200 },
            { name: "Hôtel", value: 3100 },
            { name: "Repas", value: 1600 },
            { name: "Autres", value: 900 },
        ],
        []
    );

    // ── Générateurs Excel statiques ──────────────────────────────────────────
    const handleDownloadEquipe = () => {
        const rows = [
            { Membre: "Jean Dupont", Rôle: "Développeur Senior", Contrat: "CDI", Disponibilité: "Présent", EntréeDate: "2022-03-01" },
            { Membre: "Marie Lambert", Rôle: "Designer UX", Contrat: "CDI", Disponibilité: "Congé", EntréeDate: "2021-07-15" },
            { Membre: "Paul Durand", Rôle: "Développeur Junior", Contrat: "CDD", Disponibilité: "Présent", EntréeDate: "2024-01-10" },
            { Membre: "Sophie Martin", Rôle: "Manager", Contrat: "CDI", Disponibilité: "Présent", EntréeDate: "2019-09-01" },
            { Membre: "Karim Benali", Rôle: "Développeur Full Stack", Contrat: "CDI", Disponibilité: "Mission", EntréeDate: "2023-04-20" },
        ];
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Équipe");
        XLSX.writeFile(wb, "Etat_Membres_Equipe.xlsx");
    };

    const handleDownloadConges = () => {
        const rows = [
            { Membre: "Jean Dupont", Type: "Congé annuel", DateDebut: "2026-01-10", DateFin: "2026-01-12", NbJours: 3, Statut: "En attente" },
            { Membre: "Marie Lambert", Type: "RTT", DateDebut: "2026-02-03", DateFin: "2026-02-03", NbJours: 1, Statut: "Approuvé" },
            { Membre: "Karim Benali", Type: "Congé annuel", DateDebut: "2026-03-20", DateFin: "2026-03-27", NbJours: 7, Statut: "En attente" },
        ];
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Congés");
        XLSX.writeFile(wb, "Etat_Conges_Equipe.xlsx");
    };

    const handleDownloadDepenses = () => {
        const rows = [
            { Membre: "Marie Lambert", Catégorie: "Repas client", Montant: "850 MAD", Date: "2026-01-25", Statut: "En attente" },
            { Membre: "Paul Durand", Catégorie: "Transport", Montant: "320 MAD", Date: "2026-02-10", Statut: "Remboursé" },
            { Membre: "Karim Benali", Catégorie: "Hôtel", Montant: "2 100 MAD", Date: "2026-03-01", Statut: "Remboursé" },
        ];
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Notes de frais");
        XLSX.writeFile(wb, "Etat_Notes_Frais_Equipe.xlsx");
    };

    const handleDownloadMissions = () => {
        const rows = [
            { Membre: "Paul Durand", Destination: "Casablanca", Type: "Formation", DateDebut: "2026-01-18", DateFin: "2026-01-19", Statut: "Terminée" },
            { Membre: "Karim Benali", Destination: "Rabat", Type: "Client", DateDebut: "2026-02-28", DateFin: "2026-03-01", Statut: "En cours" },
        ];
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Missions");
        XLSX.writeFile(wb, "Etat_Missions_Equipe.xlsx");
    };

    const downloadHandlers = {
        "etat-equipe": handleDownloadEquipe,
        "etat-conges": handleDownloadConges,
        "etat-depenses": handleDownloadDepenses,
        "etat-missions": handleDownloadMissions,
    };

    return (
        <div className="dmPage">
            <div className="dmWrap">
                {/* Header */}
                <div className="dmHeader">
                    <div>
                        <h1>Tableau de bord</h1>
                    </div>
                    <DateFilterFields
                        dateDebut={dateDebut}
                        dateFin={dateFin}
                        onChangeDebut={setDateDebut}
                        onChangeFin={setDateFin}
                    />
                </div>

                {/* Tab Navigation */}
                <div
                    style={{
                        display: "flex",
                        gap: 4,
                        borderBottom: "1px solid #e5e7eb",
                        marginBottom: 20,
                    }}
                >
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 7,
                                padding: "10px 18px",
                                border: "none",
                                borderBottom: activeTab === tab.id ? `2px solid ${PRIMARY}` : "2px solid transparent",
                                background: "transparent",
                                fontWeight: activeTab === tab.id ? 700 : 500,
                                color: activeTab === tab.id ? PRIMARY : "#6b7280",
                                fontSize: 14,
                                cursor: "pointer",
                                transition: "all 0.2s",
                            }}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ── DASHBOARD TAB ──────────────────────────────────────────── */}
                {activeTab === "dashboard" && (
                    <>
                        {/* KPI */}
                        <div className="dmStats">
                            <StatCard
                                label="Membres d'équipe"
                                value={teamData.totalMembers}
                                icon={<Users size={22} />}
                                color="#2f4595"
                            />
                            <StatCard
                                label="Congés en attente"
                                value={teamData.pendingLeaves}
                                icon={<Clock size={22} />}
                                color="#2f4595"
                                note="À valider"
                            />
                            <StatCard
                                label="Missions en cours"
                                value={teamData.ongoingMissions}
                                icon={<Plane size={22} />}
                                color="#2f478f"
                            />
                            <StatCard
                                label="Notes de frais"
                                value={teamData.pendingExpenses}
                                icon={<Receipt size={22} />}
                                color="#2f4595"
                                note="En attente"
                            />
                        </div>

                        {/* Charts */}
                        <div className="dmGrid">
                            <div className="dmCard">
                                <h3>Répartition des absences</h3>
                                <div className="dmChartBox">
                                    <ResponsiveContainer width="100%" height={240}>
                                        <PieChart>
                                            <Pie
                                                data={pieData}
                                                dataKey="value"
                                                nameKey="name"
                                                innerRadius={55}
                                                outerRadius={85}
                                                paddingAngle={3}
                                            >
                                                {pieData.map((_, idx) => (
                                                    <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>

                                    <div className="dmLegend">
                                        {pieData.map((d, idx) => (
                                            <div key={d.name} className="dmLegendItem">
                                                <span
                                                    className="dmLegendDot"
                                                    style={{ background: PIE_COLORS[idx % PIE_COLORS.length] }}
                                                />
                                                <span className="dmLegendName">{d.name}</span>
                                                <span className="dmLegendVal">{d.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="dmCard">
                                <h3>Dépenses par catégorie (MAD)</h3>
                                <div className="dmChartBox">
                                    <ResponsiveContainer width="100%" height={260}>
                                        <BarChart data={barData}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <Tooltip />
                                            <Bar dataKey="value" fill="#2f4595" radius={[6, 6, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        {/* Notifications */}
                        <div className="dmSectionTitle">
                            <Bell size={18} />
                            Alertes &amp; Notifications
                        </div>

                        <div className="dmCard dmNoPad">
                            {teamData.notifications.map((n) => (
                                <div key={n.id} className="dmNotifRow">
                                    <div className={`dmNotifIcon dmNotifIcon--${n.type}`}>
                                        {n.type === "leave" && <Clock size={16} />}
                                        {n.type === "expense" && <Receipt size={16} />}
                                        {n.type === "mission" && <CheckCircle size={16} />}
                                    </div>
                                    <div className="dmNotifText">
                                        <div>{n.text}</div>
                                        <div className="dmSub">{new Date().toLocaleDateString("fr-FR")}</div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="dmFooter">
                            Dernière mise à jour : {new Date().toLocaleString("fr-FR")}
                        </div>
                    </>
                )}

                {/* ── ÉTATS TAB ─────────────────────────────────────────────── */}
                {activeTab === "etats" && (
                    <>
                        <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 15, color: "#1f2937", display: "flex", alignItems: "center", gap: 8 }}>
                            <FileSpreadsheet size={18} color={PRIMARY} />
                            États téléchargeables
                        </div>
                        <p style={{ color: "#6b7280", fontSize: 13.5, marginBottom: 20 }}>
                            Téléchargez les états de votre équipe au format Excel.
                        </p>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                                gap: 18,
                            }}
                        >
                            {STATIC_EXCEL_STATES.map((state) => (
                                <ExcelStateCard
                                    key={state.id}
                                    title={state.title}
                                    description={state.description}
                                    color={state.color}
                                    onDownload={downloadHandlers[state.id]}
                                />
                            ))}
                        </div>
                        <div className="dmFooter" style={{ marginTop: 32 }}>
                            Dernière mise à jour : {new Date().toLocaleString("fr-FR")}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default DashboardManager;
